type Direction = 'up' | 'down' | 'left' | 'right'

interface Position {
  x: number
  y: number
}

export interface GameConfig {
  canvas: HTMLCanvasElement
  onScoreChange: (score: number) => void
  onGameOver: (finalScore: number) => void
}

export class Game {
  private ctx: CanvasRenderingContext2D
  private config: GameConfig

  // Game state
  private snake: Position[] = []
  private direction: Direction = 'right'
  private nextDirection: Direction = 'right'
  private food: Position = { x: 0, y: 0 }
  private score: number = 0
  private isGameOver: boolean = false
  private isPaused: boolean = false

  // Grid settings - larger cells on mobile for easier play
  private GRID_SIZE = 20
  private gridWidth: number = 0
  private gridHeight: number = 0

  // Timing
  private lastUpdate: number = 0
  private readonly TICK_RATE = 100 // ms between moves
  private animationId: number | null = null

  // Colors
  private readonly COLORS = {
    bg: '#1a1a2e',
    grid: '#16213e',
    snake: '#4ade80',
    snakeHead: '#22c55e',
    food: '#e94560',
    foodGlow: 'rgba(233, 69, 96, 0.3)'
  }

  constructor(config: GameConfig) {
    this.config = config
    this.ctx = config.canvas.getContext('2d')!
    this.resize()
    this.reset()
    this.setupInput()
  }

  private resize(): void {
    const rect = this.config.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    // Use visible viewport height to account for browser chrome
    const visibleHeight = window.visualViewport?.height || window.innerHeight
    const height = Math.min(rect.height, visibleHeight - 80)
    const width = rect.width

    this.config.canvas.width = width * dpr
    this.config.canvas.height = height * dpr
    this.ctx.scale(dpr, dpr)

    // Larger grid cells on mobile for easier play
    this.GRID_SIZE = width < 500 ? 28 : 20

    this.gridWidth = Math.floor(width / this.GRID_SIZE)
    this.gridHeight = Math.floor(height / this.GRID_SIZE)
  }

  private reset(): void {
    // Start snake in middle
    const startX = Math.floor(this.gridWidth / 2)
    const startY = Math.floor(this.gridHeight / 2)

    this.snake = [
      { x: startX, y: startY },
      { x: startX - 1, y: startY },
      { x: startX - 2, y: startY }
    ]

    this.direction = 'right'
    this.nextDirection = 'right'
    this.score = 0
    this.isGameOver = false
    this.spawnFood()
  }

  private setupInput(): void {
    // Keyboard controls
    window.addEventListener('keydown', (e) => {
      if (this.isGameOver) return

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          if (this.direction !== 'down') this.nextDirection = 'up'
          e.preventDefault()
          break
        case 'ArrowDown':
        case 's':
        case 'S':
          if (this.direction !== 'up') this.nextDirection = 'down'
          e.preventDefault()
          break
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (this.direction !== 'right') this.nextDirection = 'left'
          e.preventDefault()
          break
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (this.direction !== 'left') this.nextDirection = 'right'
          e.preventDefault()
          break
      }
    })

    // Touch controls - tap direction relative to snake head
    this.config.canvas.addEventListener('touchstart', (e) => {
      if (this.isGameOver) return
      e.preventDefault()

      const touch = e.touches[0]
      const rect = this.config.canvas.getBoundingClientRect()

      // Get tap position relative to canvas
      const tapX = touch.clientX - rect.left
      const tapY = touch.clientY - rect.top

      // Get snake head position in screen coordinates
      const head = this.snake[0]
      const headScreenX = head.x * this.GRID_SIZE + this.GRID_SIZE / 2
      const headScreenY = head.y * this.GRID_SIZE + this.GRID_SIZE / 2

      // Calculate direction from head to tap
      const deltaX = tapX - headScreenX
      const deltaY = tapY - headScreenY

      // Determine direction based on larger delta
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Horizontal - go left or right
        if (deltaX > 0 && this.direction !== 'left') {
          this.nextDirection = 'right'
        } else if (deltaX < 0 && this.direction !== 'right') {
          this.nextDirection = 'left'
        }
      } else {
        // Vertical - go up or down
        if (deltaY > 0 && this.direction !== 'up') {
          this.nextDirection = 'down'
        } else if (deltaY < 0 && this.direction !== 'down') {
          this.nextDirection = 'up'
        }
      }
    }, { passive: false })
  }

  private spawnFood(): void {
    let newFood: Position
    do {
      newFood = {
        x: Math.floor(Math.random() * this.gridWidth),
        y: Math.floor(Math.random() * this.gridHeight)
      }
    } while (this.snake.some(seg => seg.x === newFood.x && seg.y === newFood.y))

    this.food = newFood
  }

  private update(): void {
    if (this.isGameOver || this.isPaused) return

    this.direction = this.nextDirection

    // Calculate new head position
    const head = this.snake[0]
    let newHead: Position

    switch (this.direction) {
      case 'up':
        newHead = { x: head.x, y: head.y - 1 }
        break
      case 'down':
        newHead = { x: head.x, y: head.y + 1 }
        break
      case 'left':
        newHead = { x: head.x - 1, y: head.y }
        break
      case 'right':
        newHead = { x: head.x + 1, y: head.y }
        break
    }

    // Check wall collision
    if (
      newHead.x < 0 ||
      newHead.x >= this.gridWidth ||
      newHead.y < 0 ||
      newHead.y >= this.gridHeight
    ) {
      this.gameOver()
      return
    }

    // Check self collision
    if (this.snake.some(seg => seg.x === newHead.x && seg.y === newHead.y)) {
      this.gameOver()
      return
    }

    // Move snake
    this.snake.unshift(newHead)

    // Check food collision
    if (newHead.x === this.food.x && newHead.y === this.food.y) {
      this.score += 10
      this.config.onScoreChange(this.score)
      this.spawnFood()
    } else {
      this.snake.pop()
    }
  }

  private gameOver(): void {
    this.isGameOver = true
    this.config.onGameOver(this.score)
  }

  private render(): void {
    const rect = this.config.canvas.getBoundingClientRect()

    // Clear
    this.ctx.fillStyle = this.COLORS.bg
    this.ctx.fillRect(0, 0, rect.width, rect.height)

    // Draw grid (subtle)
    this.ctx.strokeStyle = this.COLORS.grid
    this.ctx.lineWidth = 0.5
    for (let x = 0; x <= this.gridWidth; x++) {
      this.ctx.beginPath()
      this.ctx.moveTo(x * this.GRID_SIZE, 0)
      this.ctx.lineTo(x * this.GRID_SIZE, this.gridHeight * this.GRID_SIZE)
      this.ctx.stroke()
    }
    for (let y = 0; y <= this.gridHeight; y++) {
      this.ctx.beginPath()
      this.ctx.moveTo(0, y * this.GRID_SIZE)
      this.ctx.lineTo(this.gridWidth * this.GRID_SIZE, y * this.GRID_SIZE)
      this.ctx.stroke()
    }

    // Draw food glow
    const foodCenterX = this.food.x * this.GRID_SIZE + this.GRID_SIZE / 2
    const foodCenterY = this.food.y * this.GRID_SIZE + this.GRID_SIZE / 2
    const gradient = this.ctx.createRadialGradient(
      foodCenterX, foodCenterY, 0,
      foodCenterX, foodCenterY, this.GRID_SIZE * 1.5
    )
    gradient.addColorStop(0, this.COLORS.foodGlow)
    gradient.addColorStop(1, 'transparent')
    this.ctx.fillStyle = gradient
    this.ctx.fillRect(
      this.food.x * this.GRID_SIZE - this.GRID_SIZE,
      this.food.y * this.GRID_SIZE - this.GRID_SIZE,
      this.GRID_SIZE * 3,
      this.GRID_SIZE * 3
    )

    // Draw food
    this.ctx.fillStyle = this.COLORS.food
    this.ctx.beginPath()
    this.ctx.arc(foodCenterX, foodCenterY, this.GRID_SIZE / 2 - 2, 0, Math.PI * 2)
    this.ctx.fill()

    // Draw snake
    this.snake.forEach((segment, index) => {
      const isHead = index === 0
      this.ctx.fillStyle = isHead ? this.COLORS.snakeHead : this.COLORS.snake

      const padding = 1
      this.ctx.beginPath()
      this.ctx.roundRect(
        segment.x * this.GRID_SIZE + padding,
        segment.y * this.GRID_SIZE + padding,
        this.GRID_SIZE - padding * 2,
        this.GRID_SIZE - padding * 2,
        isHead ? 6 : 4
      )
      this.ctx.fill()
    })
  }

  private gameLoop = (currentTime: number): void => {
    // Update at fixed tick rate
    if (currentTime - this.lastUpdate >= this.TICK_RATE) {
      this.update()
      this.lastUpdate = currentTime
    }

    this.render()

    if (!this.isGameOver) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    } else {
      this.animationId = null
    }
  }

  start(): void {
    this.lastUpdate = performance.now()
    this.animationId = requestAnimationFrame(this.gameLoop)
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  restart(): void {
    this.reset()
    this.config.onScoreChange(0)
    this.lastUpdate = performance.now()
    if (this.animationId === null) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    }
  }

  handleResize(): void {
    this.resize()
  }
}
